/**
 * @theme/facets — collection-page filter form + price range + active-facet remove buttons.
 *
 * Three custom elements:
 *   1. <facet-filters-form> — wraps filter checkboxes / sort dropdown. Debounced submit triggers a
 *      Section Rendering API fetch of the collection section, then patches the page.
 *   2. <price-range>      — min/max price inputs with mutual constraint enforcement.
 *   3. <facet-remove>     — single active-facet "remove" link (renders as a button).
 *
 * Behavior preserved verbatim. Module-level cache for filterData. popstate handler restores from cache
 * or re-fetches. Mobile drawer is rebound after re-render so toggles still work.
 */

import { Component, defineComponent } from '@theme/component';
import { debounce } from '@theme/utilities';
import { FilterUpdateEvent } from '@theme/events';

/** @type {Array<{ html: string, url: string }>} */
let filterDataCache = [];
let searchParamsInitial = window.location.search.slice(1);
let searchParamsPrev = window.location.search.slice(1);

/* Static helpers (kept module-level so popstate works without an instance). */

/**
 * @param {string} searchParams
 * @param {Event | null} [event]
 * @param {boolean} [updateURLHash=true]
 */
async function renderPage(searchParams, event = null, updateURLHash = true) {
  searchParamsPrev = searchParams;
  const sections = getSections();
  const productGrid = document.getElementById('ProductGridContainer')?.querySelector('.collection');
  productGrid?.classList.add('loading');
  document.getElementById('ProductCount')?.classList.add('loading');
  document.getElementById('ProductCountDesktop')?.classList.add('loading');

  for (const section of sections) {
    const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
    const cached = filterDataCache.find((entry) => entry.url === url);
    if (cached) {
      renderFilters(cached.html, event);
      renderProductGridContainer(cached.html);
      renderProductCount(cached.html);
    } else {
      try {
        const res = await fetch(url);
        const html = await res.text();
        filterDataCache.push({ html, url });
        renderFilters(html, event);
        renderProductGridContainer(html);
        renderProductCount(html);
      } catch (err) {
        console.error('[facets]', err);
      }
    }
  }

  if (updateURLHash) updateURLHashFn(searchParams);
  document.dispatchEvent(new FilterUpdateEvent({ url: window.location.pathname + (searchParams ? `?${searchParams}` : '') }));
}

/** @param {string} html */
function renderProductGridContainer(html) {
  const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
  const target = document.getElementById('ProductGridContainer');
  if (fresh && target) target.innerHTML = fresh.innerHTML;
}

/** @param {string} html */
function renderProductCount(html) {
  const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount')?.innerHTML;
  if (count === undefined) return;
  const container = document.getElementById('ProductCount');
  const containerDesktop = document.getElementById('ProductCountDesktop');
  if (container) { container.innerHTML = count; container.classList.remove('loading'); }
  if (containerDesktop) { containerDesktop.innerHTML = count; containerDesktop.classList.remove('loading'); }
}

/**
 * @param {string} html
 * @param {Event | null} event
 */
function renderFilters(html, event) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const facetDetails = parsed.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');

  /** @param {Element} el */
  const matchesIndex = (el) => {
    const target = event ? /** @type {HTMLElement} */ (event.target).closest('.js-filter') : null;
    return target ? /** @type {HTMLElement} */ (el).dataset.index === /** @type {HTMLElement} */ (target).dataset.index : false;
  };

  const facetsToRender = Array.from(facetDetails).filter((el) => !matchesIndex(el));
  const countsToRender = Array.from(facetDetails).find(matchesIndex);

  facetsToRender.forEach((/** @type {Element} */ el) => {
    const idx = /** @type {HTMLElement} */ (el).dataset.index;
    if (!idx) return;
    /** @type {HTMLElement | null} */
    const target = document.querySelector(`.js-filter[data-index="${idx}"]`);
    if (target) target.innerHTML = el.innerHTML;
  });

  renderActiveFacets(parsed);
  renderAdditionalElements(parsed);

  if (countsToRender && event) {
    renderCounts(countsToRender, /** @type {HTMLElement} */ (event.target).closest('.js-filter'));
  }
}

/** @param {Document} parsed */
function renderActiveFacets(parsed) {
  for (const sel of ['.active-facets-mobile', '.active-facets-desktop']) {
    const fresh = parsed.querySelector(sel);
    if (!fresh) continue;
    const target = document.querySelector(sel);
    if (target) target.innerHTML = fresh.innerHTML;
  }
  toggleActiveFacets(false);
}

/** @param {Document} parsed */
function renderAdditionalElements(parsed) {
  for (const sel of ['.mobile-facets__open', '.mobile-facets__count', '.sorting']) {
    const fresh = parsed.querySelector(sel);
    if (!fresh) continue;
    const target = document.querySelector(sel);
    if (target) target.innerHTML = fresh.innerHTML;
  }
  /** @type {any} */
  const mobileDrawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
  mobileDrawer?.bindEvents?.();
}

/**
 * @param {Element} source
 * @param {Element | null} target
 */
function renderCounts(source, target) {
  if (!target) return;
  const targetSelected = target.querySelector('.facets__selected');
  const sourceSelected = source.querySelector('.facets__selected');
  if (sourceSelected && targetSelected) targetSelected.outerHTML = sourceSelected.outerHTML;
  const targetSummary = target.querySelector('.facets__summary');
  const sourceSummary = source.querySelector('.facets__summary');
  if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;
}

/** @param {boolean} disable */
function toggleActiveFacets(disable = true) {
  document.querySelectorAll('.js-facet-remove').forEach((el) => el.classList.toggle('disabled', disable));
}

/** @param {string} searchParams */
function updateURLHashFn(searchParams) {
  history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
}

function getSections() {
  const grid = document.getElementById('product-grid');
  return grid ? [{ section: grid.dataset.id ?? '' }] : [];
}

/* popstate listener — installed once. */
window.addEventListener('popstate', (event) => {
  const params = event.state ? event.state.searchParams : searchParamsInitial;
  if (params === searchParamsPrev) return;
  renderPage(params, null, false);
});

/* -------------------------------------------------------------------------- */
/* <facet-filters-form>                                                       */
/* -------------------------------------------------------------------------- */

/**
 * @extends {Component}
 */
export class FacetFiltersForm extends Component {
  /** @type {(event: Event) => void} */ debouncedOnSubmit;

  constructor() {
    super();
    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 500);
  }

  connectedCallback() {
    super.connectedCallback();
    /** @type {HTMLFormElement | null} */
    const form = this.querySelector('form');
    form?.addEventListener('input', this.debouncedOnSubmit);

    const wrapper = this.querySelector('#FacetsWrapperDesktop');
    wrapper?.addEventListener('keyup', this.#onKeyUp);
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.code?.toUpperCase() === 'ESCAPE') {
      const details = /** @type {HTMLElement} */ (event.target).closest('details[open]');
      if (details instanceof HTMLDetailsElement) details.open = false;
    }
  };

  /** @param {HTMLFormElement} form */
  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  /**
   * @param {string} searchParams
   * @param {Event} event
   */
  onSubmitForm(searchParams, event) { renderPage(searchParams, event); }

  /** @param {Event} event */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const target = /** @type {HTMLElement} */ (event.target);
    const closestForm = /** @type {HTMLFormElement | null} */ (target.closest('form'));
    if (!closestForm) return;

    if (target.className === 'mobile-facets__checkbox') {
      this.onSubmitForm(this.createSearchParams(closestForm), event);
      return;
    }
    /** @type {string[]} */
    const forms = [];
    const isMobile = closestForm.id === 'FacetFiltersFormMobile';

    sortFilterForms.forEach((/** @type {Element} */ el) => {
      const form = /** @type {HTMLFormElement} */ (el);
      if (!isMobile) {
        if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(form.id)) {
          document.querySelectorAll('.no-js-list').forEach((n) => n.remove());
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile') {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  /** @param {Event} event */
  onActiveFilterClick = (event) => {
    event.preventDefault();
    toggleActiveFacets();
    /** @type {HTMLAnchorElement} */
    const link = /** @type {any} */ (event.currentTarget);
    const queryStart = link.href.indexOf('?');
    const url = queryStart === -1 ? '' : link.href.slice(queryStart + 1);
    renderPage(url);
  };
}

defineComponent('facet-filters-form', FacetFiltersForm);

/* -------------------------------------------------------------------------- */
/* <price-range>                                                              */
/* -------------------------------------------------------------------------- */

/**
 * @extends {Component}
 */
export class PriceRange extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('input').forEach((input) => input.addEventListener('change', this.#onChange));
    this.#setMinAndMaxValues();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    this.#adjustToValid(/** @type {HTMLInputElement} */ (event.currentTarget));
    this.#setMinAndMaxValues();
  };

  #setMinAndMaxValues() {
    /** @type {NodeListOf<HTMLInputElement>} */
    const inputs = this.querySelectorAll('input');
    const [minInput, maxInput] = [inputs[0], inputs[1]];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', '0');
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max') ?? '');
  }

  /** @param {HTMLInputElement} input */
  #adjustToValid(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min') ?? 0);
    const max = Number(input.getAttribute('max') ?? Infinity);
    if (value < min) input.value = String(min);
    if (value > max) input.value = String(max);
  }
}

defineComponent('price-range', PriceRange);

/* -------------------------------------------------------------------------- */
/* <facet-remove>                                                             */
/* -------------------------------------------------------------------------- */

/**
 * @extends {Component}
 */
export class FacetRemove extends Component {
  connectedCallback() {
    super.connectedCallback();
    /** @type {HTMLAnchorElement | null} */
    const link = this.querySelector('a');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.#close);
    link.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code?.toUpperCase() === 'SPACE') this.#close(event);
    });
  }

  /** @param {Event} event */
  #close = (event) => {
    event.preventDefault();
    /** @type {any} */
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form?.onActiveFilterClick?.(event);
  };
}

defineComponent('facet-remove', FacetRemove);
